import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, AfterViewInit, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  TRANSLOCO_SCOPE,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { AppConstants } from 'src/app/app-constants';
import {
  QaiInnerWrapComponent,
} from '@qai/ui';
import { IPracticeAssignment } from 'src/app/interfaces/reportingV2/ipractice-assignment';
import { ITeam } from 'src/app/interfaces/reportingV2/iteams';
import { IUserEngagementStatsRequest } from 'src/app/interfaces/reportingV2/iuser-engagement-stats-request';
import { IUserEngagementStatsResponse } from 'src/app/interfaces/reportingV2/iuser-engagement-stats-response';
import { IUserPracticeDetail } from 'src/app/interfaces/reportingV2/iuser-practice-details';
import { IIndividualAssignmentEngagementStatsResponse } from 'src/app/interfaces/reportingV2/iindividual-assignment-engagement-stats-response';
import { ReportingLocalStorageUtil } from 'src/app/utils/reporting-localstorage.util';
import { PracticeStatisticsComponent } from './practice-statistics/practice-statistics.component';
import { PracticeSessionsComponent } from './practice-sessions/practice-sessions.component';
import { PracticeSimulationsComponent } from './practice-simulations/practice-simulations.component';
import { PracticeUsersComponent } from './practice-users/practice-users.component';
import { PracticeHeaderComponent } from './practice-header/practice-header.component';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { PracticeAssignmentUserModalComponent } from 'src/app/pages/reporting-dashboard/components/modals/practice-assignment-user-modal/practice-assignment-user-modal.component';
import { IAssignmentViewStatsRequest } from 'src/app/interfaces/reportingV2/iassignment-view-stats-request';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { AssignmentTypeEnum } from 'src/app/utils/enums/self-serve/assignment-type.enum';
import { PRACTICE_STATUS } from 'src/app/utils/constants/reporting-dashboard/practice-status.constants';
import { withDemoModule } from 'src/app/utils/demo-mode/demo-mode-context.util';
import { ReportingNavigationResetService } from 'src/app/services/reporting-dashboard/reporting-navigation-reset.service';

@Component({
  selector: 'app-practice-detail',
  templateUrl: './practice-detail.component.html',
  styleUrls: ['./practice-detail.component.scss'],
  standalone: true,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    },
  ],
  imports: [
    TranslocoModule,
    CommonModule,
    QaiInnerWrapComponent,
    PracticeStatisticsComponent,
    PracticeSimulationsComponent,
    PracticeUsersComponent,
    PracticeSessionsComponent,
    PracticeHeaderComponent,
    PracticeAssignmentUserModalComponent,
  ],
})
export class PracticeDetailComponent implements OnInit, AfterViewInit {
  isTranslationLoaded: boolean = false;
  isUserModalVisible = false;
  isLoading: boolean = false;
  practiceId: string | null = null;
  accountUuid: string | null = null;
  practiceDetails: IPracticeAssignment | null = null;
  @ViewChild(PracticeAssignmentUserModalComponent)
  userModalComponent!: PracticeAssignmentUserModalComponent;

  @ViewChild('practiceUsersSection', { static: false })
  practiceUsersSection!: ElementRef;

  // Header component inputs
  heading: string = '';
  teamOptions: ITeam[] = [];
  selectedTeams: string[] = [];
  isLoadingTeams: boolean = false;
  engagementStatus: string | null = null;

  // User data properties
  userEngagementData: IUserPracticeDetail[] = [];
  isLoadingUsers: boolean = false;

  individualAssignmentEngagementStats: IIndividualAssignmentEngagementStatsResponse | null =
    null;
  isLoadingEngagementStats: boolean = false;


  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private translocoService: TranslocoService,
    private translationService: TranslationService,
    private reportingDashboardService: ReportingDashboardService,
    private amplitudeService: AmplitudeService,
    private readonly reportingNavigationResetService: ReportingNavigationResetService
  ) {}

  ngOnInit() {
    this.reportingNavigationResetService.init();
    console.log('PracticeDetailComponent initialized');
    this.accountUuid = ReportingLocalStorageUtil.getStoredAccountUuid();
    this.practiceId = this.route.snapshot.paramMap.get('id');
    this.engagementStatus =
      this.route.snapshot.queryParamMap.get('engagement_status');
    this.selectedTeams = ReportingLocalStorageUtil.getStoredTeamsFilter(this.accountUuid ?? undefined) ?? [];

    const state = history.state;
    if (state) {
      this.heading = state['heading'] || '';
    }

    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.REPORTINGDASHBOARD_INDIVIDUAL_ASSIGNMENT_PAGEVIEW, {
          assignment_name: this.heading || 'Unknown',
          assignment_type: AssignmentTypeEnum.PRACTICE,
    });

    this.translationService.loadScopedTranslation('reportingV2').then(() => {
      this.isTranslationLoaded = true;
    });
    
    this.loadPracticeDetails();
    this.loadTeams();
    this.loadUserEngagementData();
    this.loadIndividualAssignmentEngagementStats();
  }

  scrollToTop(): void {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant',
    });
  }

  ngAfterViewInit() {
    if (this.engagementStatus) {
      setTimeout(() => {
        this.scrollToPracticeUsers();
      }, 2000);
    } else {
      this.scrollToTop();
    }
  }

  private scrollToPracticeUsers() {
    if (this.practiceUsersSection) {
      // First, scroll to the element using scrollIntoView
      this.practiceUsersSection.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });

      // Then, after a short delay, ensure it's positioned at the very top
      setTimeout(() => {
        const marginTop = 20;
        const elementTop =
          this.practiceUsersSection.nativeElement.offsetTop - marginTop;
        window.scrollTo({
          top: elementTop,
          behavior: 'smooth',
        });
      }, 200);
    }
  }

  private loadPracticeDetails() {
    // TODO: Implement API call to fetch practice details
  }

  private loadUserEngagementData() {
    const accountUuid = ReportingLocalStorageUtil.getStoredAccountUuid();

    if (!accountUuid || !this.practiceId) {
      console.warn('Missing required data for user engagement API call');
      return;
    }

    this.isLoadingUsers = true;

    const request: IUserEngagementStatsRequest = {
      accountUuid: accountUuid,
      moduleAssignmentUuid: this.practiceId,
      teamUuids: this.selectedTeams,
    };

    this.reportingDashboardService.getUserEngagementStats(request, withDemoModule(AppConstants.NEW_REPORTING_UI)).subscribe({
      next: (response: IUserEngagementStatsResponse[]) => {
        this.userEngagementData =
          this.transformApiResponseToUserPracticeDetails(response);
        this.isLoadingUsers = false;
      },
      error: (error) => {
        console.error('Error loading user engagement data:', error);
        this.isLoadingUsers = false;
      },
    });
  }

  private transformApiResponseToUserPracticeDetails(
    apiResponse: IUserEngagementStatsResponse[]
  ): IUserPracticeDetail[] {
    return apiResponse.map((user, index) => ({
      id: user.uuid || `user-${index}`,
      userName: user.userName,
      email: user.email,
      teams: user.teams
        ? user.teams.split(', ').map((team) => team.trim())
        : [],
      engagementStatus: this.normalizeEngagementStatus(
        user.engagementStatus || ''
      ),
      numberOfSessions: user.numberOfSessions,
      initialScore: user.initialScore || '0',
      highScore: user.highScore || '0',
      averageScore: user.averageScore || '0',
      percentImproved: user.percentImproved || '0',
    }));
  }

  private normalizeEngagementStatus(status: string): string {
    const normalizedStatus = status.toLowerCase();

    if (normalizedStatus === PRACTICE_STATUS.NOT_STARTED || normalizedStatus === PRACTICE_STATUS.ASSIGNED) {
      return this.translocoService.translate('reportingV2.ASSIGNED_LABEL');
    } else if (normalizedStatus === PRACTICE_STATUS.ENGAGED) {
      return this.translocoService.translate('reportingV2.ENGAGED_LABEL');
    } else if (normalizedStatus === PRACTICE_STATUS.PROFICIENT) {
      return this.translocoService.translate('reportingV2.PROFICIENT_LABEL');
    }

    return status;
  }

  private loadTeams() {
    this.isLoadingTeams = true;
    const accountUuid = ReportingLocalStorageUtil.getStoredAccountUuid();

    if (!accountUuid || !this.practiceId) {
      console.warn('Missing required data for user engagement API call');
      return;
    }

    this.reportingDashboardService.getTeams(accountUuid, withDemoModule(AppConstants.NEW_REPORTING_UI)).subscribe({
      next: (teams) => {
        this.teamOptions = teams;
        this.isLoadingTeams = false;
      },
      error: (error) => {
        console.error('Error loading teams:', error);
        this.isLoadingTeams = false;
      },
    });
  }

  private loadIndividualAssignmentEngagementStats() {
    const accountUuid = ReportingLocalStorageUtil.getStoredAccountUuid();

    if (!accountUuid || !this.practiceId) {
      console.warn(
        'Missing required data for individual assignment engagement stats API call'
      );
      return;
    }

    this.isLoadingEngagementStats = true;

    const request: IAssignmentViewStatsRequest = {
      accountUuid: accountUuid,
      moduleAssignmentUuid: this.practiceId,
      teamUuids: this.selectedTeams.includes('all') ? [] : this.selectedTeams,
    };

    this.reportingDashboardService
      .getIndividualAssignmentEngagementStatus(request, withDemoModule(AppConstants.NEW_REPORTING_UI))
      .subscribe({
        next: (response: IIndividualAssignmentEngagementStatsResponse) => {
          this.individualAssignmentEngagementStats = response;
          this.isLoadingEngagementStats = false;
        },
        error: (error) => {
          console.error(
            'Error loading individual assignment engagement stats:',
            error
          );
          this.isLoadingEngagementStats = false;
        },
      });
  }

  goToDashboard() {
    this.router.navigate(['/reporting_dashboard']);
  }

  onTeamChange(teams: string[]) {
    this.selectedTeams = teams;
    if (this.accountUuid) {
      ReportingLocalStorageUtil.setStoredTeamsFilter(teams, this.accountUuid);
    }
    this.loadUserEngagementData();
    this.loadIndividualAssignmentEngagementStats();
  }

  openUserModal() {
    this.isUserModalVisible = true;
  }

  closeUserModal() {
    this.isUserModalVisible = false;
  }
}
